import React from 'react';

import classNames from 'classnames';

import type { Theme } from '@material-ui/core';
import { AppBar, Portal, Toolbar } from '@material-ui/core';
import type { AppDefinition } from '@klp/core/app/classes/AppDefinition';
import { AppRoutes } from '@klp/core/app/config';

import { Link } from '@klp/design/Link';
import { Logo } from '@klp/design/Logo';
import type { KlpStyled } from '@klp/design/Theme';

import { HeaderToolbar } from './components/HeaderToolbar';
import { HeaderContextSelector } from './components/HeaderContextSelector';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { useLayoutContext } from '../Layout';

const headerStyles = (theme: Theme) => ({
    appBarContainer: {
        height: theme.header.height,
    },
    appBar: {
        color: theme.palette.primary.main,
        backgroundColor: theme.palette.background.paper,
        zIndex: theme.zIndex.drawer - 3,
        width: '100%',
        height: theme.header.height,
        boxShadow: theme.shadows[1],
        transition: theme.layout.transition,
    },

    appBarToolbar: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    },
    logo: {
        width: theme.sidebar.closed.width,
        fill: theme.palette.primary.dark,
        transition: theme.layout.transition,
        display: 'flex',
        justifyContent: 'center',
        '&:before': {
            display: 'none',
        },
        '& svg': {
            width: '52px',
            height: 'auto',
        },
        '&:hover': {
            fill: theme.palette.secondary.dark,
        },
    },
    breadcrumbs: {
        display: 'flex',
        flex: '5 1 0',
        marginLeft: theme.spacing(2),
        minWidth: 0,
    },
    contextSelector: {
        display: 'flex',
        flex: '6 1 0',
        minWidth: 0,
        alignItems: 'center',
    },
    appBarToolbarRight: {
        display: 'flex',
        flex: '1 1 0',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end',
        '& > *': {
            marginLeft: '0.5em',
        },
    },
    withSidebar: {
        paddingLeft: theme.sidebar.closed.width,
    },
    withSidebarAndMenuPin: {
        paddingLeft: theme.sidebar.open.width,
    },
    mobile: {
        paddingLeft: 0,
        '& .Header__Breadcrumbs': {
            display: 'none',
        },
    },
    screen: {
        '&.Header--withSidebar': {
            '& .Header__Logo': {
                display: 'none',
            },
        },
    },
});

interface HeaderOwnProps {
    appSelection: Array<AppDefinition>;
    breadcrumbsComponent: React.ComponentType<KlpStyled>;
}

export const Header: React.FC<HeaderOwnProps> = ({
    appSelection,
    breadcrumbsComponent: CustomBreadcrumbs,
}) => {
    const headerRef = React.createRef<HTMLDivElement>();

    const classes = useKlpStyles({}, headerStyles);

    const { withoutSidebar, menuPin, isMobile } = useLayoutContext();

    return (
        <div
            className={classNames('Header', classes.appBarContainer)}
            data-testid="Header"
        >
            <AppBar
                className={classNames(classes.appBar, {
                    'Header--withSidebar': !withoutSidebar,
                    [classes.withSidebar]: !withoutSidebar,
                    [classes.withSidebarAndMenuPin]: menuPin,
                    [classes.mobile]: isMobile,
                    [classes.screen]: !isMobile,
                })}
                position="fixed"
                ref={headerRef}
                elevation={4}
            >
                <Toolbar
                    className={classes.appBarToolbar}
                    disableGutters={true}
                >
                    <Link
                        className={classNames('Header__Logo', classes.logo)}
                        href={AppRoutes.mainPage()}
                    >
                        <Logo />
                    </Link>

                    <div
                        className={classNames(
                            'Header__Breadcrumbs',
                            classes.breadcrumbs,
                        )}
                    >
                        <CustomBreadcrumbs />
                    </div>

                    <div className={classes.contextSelector}>
                        <HeaderContextSelector headerRef={headerRef} />
                    </div>

                    <div className={classes.appBarToolbarRight}>
                        <HeaderToolbar appSelection={appSelection} />
                    </div>
                </Toolbar>

                <Portal container={headerRef.current} />
            </AppBar>
        </div>
    );
};
